import { isAllyView } from '@/lib/ally-view';

/** Default signed-in landing. The work canvas. */
export const DASHBOARD_HOME_PATH = '/dashboard';

/** Ally chat. Kept warm in the dashboard shell so opening it feels like the old tab. */
export const DASHBOARD_CHAT_PATH = '/dashboard/chat';

/** Ally docs library. Its own page, linked from the minimal sidebar. */
export const DASHBOARD_DOCS_PATH = '/dashboard/docs';

/** Legacy overview URL kept as a compatibility redirect. */
export const DASHBOARD_OVERVIEW_PATH = '/dashboard/overview';

/** Legacy Ally workspace. Redirects to chat or docs. */
export const ALLY_HOME_PATH = '/dashboard/ally';

/** Ally docs library. */
export const ALLY_DOCS_PATH = DASHBOARD_DOCS_PATH;

const LEGACY_CHAT_KEYS = new Set([
  'tab',
  'view',
  'thread',
  'q',
  'allyReply',
  'allyNotice',
]);

export function isDashboardChatPath(pathname: string | null | undefined): boolean {
  const path = (pathname ?? '').replace(/\/$/, '') || '/';
  return path === DASHBOARD_CHAT_PATH || path.startsWith(`${DASHBOARD_CHAT_PATH}/`);
}

/** Work canvas. Exact match so `/dashboard/chat` and other sections stay separate. */
export function isDashboardHomePath(pathname: string | null | undefined): boolean {
  const path = (pathname ?? '').replace(/\/$/, '') || '/';
  return path === DASHBOARD_HOME_PATH;
}

export function allyChatHref(
  query: Record<string, string | undefined> = {},
): string {
  if (query.view === 'docs') return DASHBOARD_DOCS_PATH;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (!value) continue;
    if (key === 'view' && (value === 'new-tasks' || value === 'docs')) continue;
    params.set(key, value);
  }
  const qs = params.toString();
  return qs ? `${DASHBOARD_CHAT_PATH}?${qs}` : DASHBOARD_CHAT_PATH;
}

/**
 * Old home URLs (`?tab=chat`, `?view=docs`, `?thread=`) now live on their own
 * pages. Returns null when the home page should render.
 */
export function legacyHomeChatRedirect(
  params: Record<string, string | undefined>,
): string | null {
  if (params.view === 'docs') return appendPreservedParams(DASHBOARD_DOCS_PATH, params);
  const view = params.view && isAllyView(params.view) ? params.view : undefined;
  const wantsChat =
    params.tab === 'chat' ||
    !!params.thread ||
    !!params.q ||
    (!!view && view !== 'new-tasks' && view !== 'docs');
  if (!wantsChat) return null;
  return appendPreservedParams(
    allyChatHref({
      view: view && view !== 'docs' ? view : undefined,
      thread: params.thread,
      q: params.q,
      allyReply: params.allyReply,
      allyNotice: params.allyNotice,
    }),
    params,
  );
}

function appendPreservedParams(
  href: string,
  params: Record<string, string | undefined>,
): string {
  const url = new URL(href, 'https://link.ai');
  for (const [key, value] of Object.entries(params)) {
    if (!value || LEGACY_CHAT_KEYS.has(key)) continue;
    if (!url.searchParams.has(key)) url.searchParams.set(key, value);
  }
  const qs = url.searchParams.toString();
  return qs ? `${url.pathname}?${qs}` : url.pathname;
}
